import { useQueryParams } from '@app/hooks/useQuestionaireRoute';

import { downloadFile } from '@app/hooks/useDownload';
import { useNavigation } from '@app/hooks/useNavigation';
import { parseJwt, SignatureRequestToken } from '@app/utils/parseJwt';

import { FileView } from '@components/block/FileView/FileView';
import LangSelect from '@components/block/Header/LangSelect';
import { Logo } from '@components/block/Header/Logo';
import OutputView from '@components/block/OutputView/OutputView';
import { Button } from '@components/ui';
import { useFileDownloadQuery } from '@dieterApi/file/useFileDownloadQuery';
import { useOpenQuestionnaireMutation } from '@dieterApi/questionnaire/useOpenQuestionnaireMutation';
import { useQuestionnaireDownloadPdfV2Mutation } from '@dieterApi/questionnaire/useQuestionnaireDownloadPdfV2Mutation';
import { useGetShareTokenJWTQuery } from '@dieterApi/sharetoken/useGetShareTokenJWTQuery';
import { useSignatureRequestQuery } from '@dieterApi/signature/useSignatureRequestQuery';
import { Modal, Paper } from '@mui/material';
import { Download } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AttachmentsModal } from './AttachmentsModal';
import './documentshare.sass';
import { SignatureButton } from './SignatureButton';
import { SignatureSuccess } from './SignatureSuccess';

export interface RouteParams {
  localQuestionnaireId?: string;
}

export interface RouteParamsWithQuery extends RouteParams {
  documentNumber?: number;
}

export default function DocumentSign() {
  const { t } = useTranslation();
  const query = useQueryParams();

  const { navigation, setNavigation } = useNavigation();
  const token = query.get('token') || '';
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);

  const { data: shareTokenJWT, loading: tokenLoading } = useGetShareTokenJWTQuery({
    variables: { id: token },
  });

  const [getSignatureRequest, { data: signatureRequestData, loading: requestLoading }] = useSignatureRequestQuery();
  const [openQuestionnaire] = useOpenQuestionnaireMutation();
  const [downloadPdf, { loading: downloadingPdf }] = useQuestionnaireDownloadPdfV2Mutation();
  const [downloadFileQuery] = useFileDownloadQuery();
  const loading = requestLoading || tokenLoading;
  const jwt = shareTokenJWT?.getShareTokenJWT?.jwt;
  const signatureRequest = signatureRequestData?.getSignatureRequest;
  const tokenInfo = jwt ? parseJwt<SignatureRequestToken>(jwt)! : undefined;

  const [accepted, setAccepted] = useState(false);

  const handleDownloadQuestionnaire = async () => {
    if (!tokenInfo?.localQuestionnaireId) return;
    try {
      // First, validate/recreate questionnaire on legalOS if needed
      await openQuestionnaire({
        variables: { localQuestionnaireId: tokenInfo.localQuestionnaireId, token: jwt },
      });
      // V2 mutation uses local ID for HTML-first rendering
      const result = await downloadPdf({
        variables: {
          id: tokenInfo.localQuestionnaireId,
          token: jwt,
          fileName: `${tokenInfo.documentLabel} ${tokenInfo.companyName}.pdf`,
        },
      });
      const downloadData = result?.data?.questionnaireDownloadPdfV2;
      if (downloadData) {
        downloadFile(downloadData);
      }
    } catch (error) {
      console.error('Error downloading questionnaire:', error);
    }
  };

  const handleDownloadFile = () => {
    if (!tokenInfo?.contractFileId || !jwt) return;
    downloadFileQuery({ variables: { id: tokenInfo.contractFileId, token: jwt } });
  };

  // There is a timing problem in setting this to true
  // apparently the navigation context is not yet set
  useEffect(() => {
    if (navigation.isPrintView) return;
    setNavigation((nav) => void (nav.isPrintView = true));
  }, [navigation]);

  useEffect(() => {
    return () => {
      setNavigation((nav) => void (nav.isPrintView = false));
    };
  }, []);

  useEffect(() => {
    if (jwt) {
      getSignatureRequest({ variables: { token: jwt } });
    }
  }, [jwt]);

  const handleClick = () => {
    setAccepted(true);
  };

  if (loading) {
    return <div className="dtDocumentShare flex flex-col gap-[10px] h-[400px]"></div>;
  }

  // find out if signatureRequest was already signed by the user
  // first find out which signee correspondes to the tokenInfo
  let hasSigned = null;
  switch (tokenInfo?.signerId) {
    case signatureRequest?.signee1.id:
      hasSigned = signatureRequest?.hasSigned1 || false;
      break;
    case signatureRequest?.signee2.id:
      hasSigned = signatureRequest?.hasSigned2 || false;
      break;
    default:
      console.warn('SignatureRequest: No matching signee found for tokenInfo');
      break;
  }

  return !accepted ? (
    <div className="dtDocumentShare flex flex-col gap-[10px] h-full!">
      <div className="fixed top-6 right-6">
        <LangSelect inHeader={false} />
      </div>
      <div className="header flex gap-[10px]">
        <h1 className="text-3xl font-medium">Dokument zur Unterschrift</h1>
        <Logo clickable={false} variant="light" />
      </div>
      {tokenInfo ? (
        <div className="flex flex-col gap-10">
          <p>Hallo {tokenInfo.signerName}!</p>
          <p>
            Das Unternehmen <strong>{tokenInfo.companyName}</strong> bittet dich das Dokument{' '}
            <strong>{tokenInfo.documentLabel}</strong> zu unterschreiben.
          </p>
          {!signatureRequest ? (
            <p className="text-red-600 font-semibold bg-red-100 p-4 rounded">
              Leider ist die Anfrage bereits abgelaufen oder ungültig.
            </p>
          ) : (
            <>
              {hasSigned && <SignatureSuccess />}
              <Button onClick={handleClick} data-testid={'button-submit'}>
                Dokument einsehen
              </Button>
            </>
          )}
        </div>
      ) : (
        <p>Jemand hat versucht ein Dokument zur Unterschrift freizugeben. Leider ist der Token ungültig.</p>
      )}
    </div>
  ) : (
    <div>
      <Modal
        open={true}
        hideBackdrop={true}
        disableEnforceFocus={true}
        data-testid="doc-modal"
        disablePortal
        sx={{ zIndex: '40' }}
      >
        <div className="">
          <Paper
            className="absolute top-1/2 left-1/2 overflow-y-auto overflow-x-hidden h-[95vh] md:h-[90vh] w-[95vw] md:w-[80vw] max-w-[800px]  md:mt-[10px] lg:mt-4 -translate-y-1/2 -translate-x-1/2 z-30 pointer-events-auto flex flex-col"
            elevation={10}
          >
            <div className="flex-1">
              {tokenInfo?.localQuestionnaireId && (
                <OutputView
                  localQuestionnaireId={tokenInfo.localQuestionnaireId}
                  documentNumber={0}
                  token={jwt}
                  consumption={false}
                />
              )}
              {tokenInfo?.contractFileId && <FileView fileId={tokenInfo.contractFileId} token={jwt} />}
            </div>
            {/* Sticky Download and Attachments Links above signature button */}
            <div className="sticky bottom-20 w-full z-50 flex justify-center gap-4">
              <button
                className="underline text-primary-root bg-white px-4 py-2 rounded shadow border border-primary-root mb-2 mt-2 text-sm flex items-center gap-2"
                onClick={tokenInfo?.localQuestionnaireId ? handleDownloadQuestionnaire : handleDownloadFile}
                disabled={downloadingPdf}
                data-testid="button-download"
                style={{ cursor: 'pointer' }}
              >
                <Download size={14} />
                {downloadingPdf ? 'Wird heruntergeladen...' : 'Dokument herunterladen'}
              </button>
              {(tokenInfo?.subcontractors?.length || tokenInfo?.tomsQuestionnaireId) && (
                <button
                  className="underline text-primary-root bg-white px-4 py-2 rounded shadow border border-primary-root mb-2 mt-2 text-sm"
                  onClick={() => setAttachmentsOpen(true)}
                  data-testid="button-attachments"
                  style={{ cursor: 'pointer' }}
                >
                  Anlagen anzeigen
                </button>
              )}
            </div>
            {jwt && tokenInfo && <SignatureButton jwt={jwt} tokenInfo={tokenInfo} disabled={!!hasSigned} />}
          </Paper>
        </div>
      </Modal>
      <AttachmentsModal
        open={attachmentsOpen}
        onClose={() => setAttachmentsOpen(false)}
        token={jwt || ''}
        tokenInfo={tokenInfo!}
      />
    </div>
  );
}
